if (!customElements.get('lsg-cards-slider')) {
  class LsgCardsSlider extends HTMLElement {

    constructor() {
      super();
      this.classes = {
        sliderComponent: 'lsg-cards-slider__slider-component',
        progressFill: 'lsg-cards-slider__progress-fill',
        card: 'lsg-card',
        cardPanel: 'lsg-card__panel',
        cardAsideMedia: 'lsg-card__aside-media',
        cardProductNameLink: 'lsg-card__product-name',
        isDragging: 'is-dragging',
        isExpanded: 'is-expanded',
        cardDataAttr: 'data-lsg-card',
        cardToggleAttr: 'data-lsg-card-toggle',
      }
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this._abort = new AbortController();
      this._mqHover = window.matchMedia('(hover: hover) and (pointer: fine)');

      this._component = this.querySelector(`slider-component.${this.classes.sliderComponent}`);
      this._fill = this.querySelector(`.${this.classes.progressFill}`);
      this._slider = this._component?.querySelector('[id^="Slider-"]') || null;

      if (this._slider) this._initMouseDragScroll(this._slider);
      this._initCardToggles();

      // Progress + smooth nav are only relevant when slider exists.
      if (this._component) {
        this._initProgress();
        this._initSmoothSliderNav();
      }
    }

    disconnectedCallback() {
      if (this._abort) this._abort.abort();
      this._abort = null;
      this._component = null;
      this._fill = null;
      this._slider = null;
    }

    _applyProgress(detail) {
      if (!this._fill) return;
      const max = detail.scrollWidth - detail.clientWidth;
      const ratio = detail.clientWidth / detail.scrollWidth;
      if (max <= 0) {
        this._fill.style.width = '100%';
        this._fill.style.marginLeft = '0';
        return;
      }
      const p = detail.scrollLeft / max;
      this._fill.style.width = `${ratio * 100}%`;
      this._fill.style.marginLeft = `${p * (100 - ratio * 100)}%`;
    }

    _initProgress() {
      if (!this._component || !this._fill) return;

      this._component.addEventListener(
        'theme:sliderScroll',
        (event) => {
          if (event.detail) this._applyProgress(event.detail);
        },
        { signal: this._abort.signal }
      );

      customElements.whenDefined('slider-component').then(() => {
        if (this._abort.signal.aborted) return;
        requestAnimationFrame(() => {
          if (this._abort.signal.aborted) return;
          if (this._component?.slider) {
            this._applyProgress({
              scrollLeft: this._component.slider.scrollLeft,
              scrollWidth: this._component.slider.scrollWidth,
              clientWidth: this._component.slider.clientWidth,
            });
          }
        });
      });
    }

    _initMouseDragScroll(slider) {
      if (!slider || slider.dataset.lsgDragScroll === 'true') return;
      slider.dataset.lsgDragScroll = 'true';

      let dragging = false;
      let startX = 0;
      let startScroll = 0;
      let moved = false;

      const interactiveTarget = (el) =>
        el.closest(
          `a[href], button, input, textarea, select, label, [role="button"], [${this.classes.cardToggleAttr}]`
        );

      slider.addEventListener(
        'pointerdown',
        (e) => {
          if (e.pointerType !== 'mouse' || e.button !== 0) return;
          if (interactiveTarget(e.target)) return;

          dragging = true;
          moved = false;
          startX = e.clientX;
          startScroll = slider.scrollLeft;
          slider.classList.add(this.classes.isDragging);
          try {
            slider.setPointerCapture(e.pointerId);
          } catch (_) {}
        },
        { capture: true, signal: this._abort.signal }
      );

      slider.addEventListener(
        'pointermove',
        (e) => {
          if (!dragging || e.pointerType !== 'mouse') return;
          const dx = e.clientX - startX;
          if (Math.abs(dx) > 2) {
            moved = true;
            e.preventDefault();
            slider.scrollLeft = startScroll - dx;
          }
        },
        { passive: false, signal: this._abort.signal }
      );

      const endDrag = (e) => {
        if (!dragging) return;
        dragging = false;
        slider.classList.remove(this.classes.isDragging);
        try {
          if (e.pointerId != null) slider.releasePointerCapture(e.pointerId);
        } catch (_) {}

        if (moved) {
          const stopClick = (ev) => {
            ev.preventDefault();
            ev.stopImmediatePropagation();
          };
          slider.addEventListener('click', stopClick, { capture: true, signal: this._abort.signal });
          setTimeout(() => slider.removeEventListener('click', stopClick, true), 0);
        }
      };

      slider.addEventListener('pointerup', endDrag, { signal: this._abort.signal });
      slider.addEventListener('pointercancel', endDrag, { signal: this._abort.signal });
    }

    _initCardToggles() {
      this.querySelectorAll(`[${this.classes.cardDataAttr}]`).forEach((card) => {
        const toggle = card.querySelector(`[${this.classes.cardToggleAttr}]`);
        const panel = card.querySelector(`.${this.classes.cardPanel}`);
        if (!toggle) return;

        const syncAria = () => {
          const expanded = card.classList.contains(this.classes.isExpanded);
          toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        };

        const isExcludedCollapseTarget = (target) =>
          Boolean(
            target.closest(`.${this.classes.cardAsideMedia} a`) ||
              target.closest(`a.${this.classes.cardProductNameLink}`)
          );

        const collapse = () => {
          if (!card.classList.contains(this.classes.isExpanded)) return;
          card.classList.remove(this.classes.isExpanded);
          syncAria();
        };

        const expand = () => {
          if (card.classList.contains(this.classes.isExpanded)) return;
          // Only one expanded card at a time on mobile.
          this.querySelectorAll(`[${this.classes.cardDataAttr}].${this.classes.isExpanded}`).forEach((other) => {
            if (other === card) return;
            other.classList.remove(this.classes.isExpanded);
            const otherToggle = other.querySelector(`[${this.classes.cardToggleAttr}]`);
            if (otherToggle) otherToggle.setAttribute('aria-expanded', 'false');
          });
          card.classList.add(this.classes.isExpanded);
          syncAria();
        };

        card.addEventListener(
          'click',
          (e) => {
            if (this._mqHover.matches) return;
            if (isExcludedCollapseTarget(e.target)) return;

            if (card.classList.contains(this.classes.isExpanded)) {
              collapse();
              return;
            }

            if (panel && e.target.closest(`.${this.classes.cardPanel}`)) expand();
          },
          { signal: this._abort.signal }
        );

        syncAria();
      });
    }

    _initSmoothSliderNav() {
      const component = this._component;
      if (!component || component.dataset.lsgSmoothNav === 'true') return;

      const prev = component.querySelector('button[name="previous"]');
      const next = component.querySelector('button[name="next"]');
      if (!prev || !next) return;
      component.dataset.lsgSmoothNav = 'true';

      const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const onNavClick = (e, btn) => {
        if (btn.hasAttribute('disabled')) return;
        e.preventDefault();
        e.stopImmediatePropagation();

        const slider = component.slider;
        if (!slider) return;
        const offset = component.sliderItemOffset;
        if (offset == null || Number.isNaN(offset)) return;

        const step = Number(btn.dataset.step || 1);
        const delta = step * offset;
        const position = btn.name === 'next' ? slider.scrollLeft + delta : slider.scrollLeft - delta;
        const behavior = reducedMotion() ? 'auto' : 'smooth';
        slider.scrollTo({ left: position, behavior });
      };

      customElements.whenDefined('slider-component').then(() => {
        if (this._abort.signal.aborted) return;
        requestAnimationFrame(() => {
          if (this._abort.signal.aborted) return;
          [prev, next].forEach((btn) => {
            btn.addEventListener('click', (e) => onNavClick(e, btn), {
              capture: true,
              signal: this._abort.signal,
            });
          });
        });
      });
    }
  }

  customElements.define('lsg-cards-slider', LsgCardsSlider);
}
